// What a link placement's card shows instead of a public URL and QR (email
// spec §8.1): the id a sender configures, the invite URL scheme it builds
// links from, the last 30 days' invited and answered counts, and the way to
// the mail markup. There is no link to open: a link placement is reachable
// only through its invites.

import { Button, CopyButton } from "@pej/kore/components";
import { useEmailDocument, useInviteSummary } from "@/api/invites";
import type { Placement } from "@/api/types";

export function LinkPlacementView({
    placement,
    onOpenEmailBlock,
}: {
    placement: Placement;
    onOpenEmailBlock: () => void;
}) {
    // The scheme is the server's to publish (email spec §3); the card only
    // shows it. Relative forms, from a local setup, are shown on this origin.
    const document = useEmailDocument(placement.id);
    const absolute = (form: string) => (form.startsWith("/") ? `${window.location.origin}${form}` : form);
    const scheme = document.data
        ? { invite: absolute(document.data.urlScheme.invite), choice: absolute(document.data.urlScheme.choice) }
        : null;
    const summary = useInviteSummary(placement.id);
    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium">Placement id</p>
                <code className="rounded-kore-md bg-kore-bg px-2 py-1 text-sm">{placement.id}</code>
                <CopyButton value={placement.id} label="Copy placement id" />
                <Button type="button" variant="outline" size="sm" onClick={onOpenEmailBlock}>
                    Email block
                </Button>
            </div>
            <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">Invite links</p>
                <code className="truncate rounded-kore-md border border-kore-border-soft bg-kore-bg px-3 py-2 text-sm">
                    {scheme?.invite ?? "…"}
                </code>
                <code className="truncate rounded-kore-md border border-kore-border-soft bg-kore-bg px-3 py-2 text-sm">
                    {scheme?.choice ?? "…"}
                </code>
                <p className="text-sm text-kore-text-muted">
                    A sender mints invites against the placement id and builds every link from the token; the second
                    form proposes a first-step answer, which the guest confirms with a tap.
                </p>
            </div>
            <dl className="flex gap-6 text-sm" aria-label="Invites in the last 30 days">
                <div>
                    <dt className="text-kore-text-muted">Invited, 30 days</dt>
                    <dd className="font-medium">{summary.data ? summary.data.invited : "—"}</dd>
                </div>
                <div>
                    <dt className="text-kore-text-muted">Answered</dt>
                    <dd className="font-medium">{summary.data ? summary.data.answered : "—"}</dd>
                </div>
            </dl>
        </div>
    );
}
